/**
 * <s-gallery-color-sync> — Keeps the product gallery and the Color option in step.
 *
 * - Swiping the mobile gallery to a photo that belongs to a color selects that color
 *   in the variant picker (which then updates price, size availability and the URL).
 * - Choosing a color scrolls the mobile gallery to that color's photo, and on desktop
 *   promotes that photo to the large featured slot.
 * Photos with no color (size guide, lifestyle shots) never change the selection.
 *
 * Element dataset:
 *   data-color-option — Exact name of the product's Color option (radio/select `name`).
 *   data-product-id   — Matches [data-product-form][data-product-id] holding the picker.
 *
 * Required child markup:
 *   [data-product-gallery]                   — The gallery scroller / grid.
 *   [data-media-id][data-media-color]        — Each photo that belongs to one color.
 *   Featured photo carries `md:col-span-2` and inline `order:-1`.
 */
const FEATURED_CLASS = 'md:col-span-2';
const SWIPE_SETTLE_MS = 220;
const PROGRAMMATIC_SCROLL_MS = 900;

class SGalleryColorSync extends HTMLElement {
  #ac;
  #observer;
  #timer;
  #suppressUntil = 0;
  #selectingFromSwipe = false;

  connectedCallback() {
    const optionName = this.dataset.colorOption;
    const gallery = this.querySelector('[data-product-gallery]');
    if (!optionName || !gallery) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const productId = this.dataset.productId || '';
    const form = document.querySelector(`[data-product-form][data-product-id="${CSS.escape(productId)}"]`);
    if (!form) return;

    const desktop = window.matchMedia('(min-width: 768px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const isScroller = () => gallery.scrollWidth > gallery.clientWidth + 1;

    const currentColor = () => {
      const name = CSS.escape(optionName);
      const radio = form.querySelector(`input[type="radio"][name="${name}"]:checked`);
      if (radio) return radio.value;
      return form.querySelector(`select[name="${name}"]`)?.value ?? null;
    };

    const selectColor = (color) => {
      const name = CSS.escape(optionName);
      const select = form.querySelector(`select[name="${name}"]`);
      let control = null;
      if (select) {
        if (![...select.options].some((option) => option.value === color)) return;
        select.value = color;
        control = select;
      } else {
        control = [...form.querySelectorAll(`input[type="radio"][name="${name}"]`)]
          .find((input) => input.value === color);
        if (!control || control.disabled) return;
        control.checked = true;
      }
      this.#selectingFromSwipe = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
      this.#selectingFromSwipe = false;
    };

    // Swipe → color. Only meaningful while the gallery is a horizontal scroller (mobile).
    this.#observer = new IntersectionObserver((entries) => {
      const settled = entries.find((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6);
      if (!settled) return;
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => {
        if (!isScroller() || Date.now() < this.#suppressUntil) return;
        const color = settled.target.dataset.mediaColor;
        if (color && color !== currentColor()) selectColor(color);
      }, SWIPE_SETTLE_MS);
    }, { root: gallery, threshold: [0.6] });

    gallery.querySelectorAll('[data-media-color]').forEach((item) => this.#observer.observe(item));

    // Color → gallery.
    form.addEventListener('change', (event) => {
      const control = event.target;
      if (this.#selectingFromSwipe || control?.name !== optionName) return;
      const color = control.value;
      const item = [...gallery.querySelectorAll('[data-media-color]')]
        .find((el) => el.dataset.mediaColor === color);
      if (!item) return;

      if (isScroller()) {
        this.#suppressUntil = Date.now() + PROGRAMMATIC_SCROLL_MS;
        const offset = item.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
        gallery.scrollTo({ left: gallery.scrollLeft + offset, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } else if (desktop.matches) {
        const featured = gallery.querySelector(`[data-media-id].${CSS.escape(FEATURED_CLASS)}`);
        if (featured === item) return;
        if (featured) {
          featured.classList.remove(FEATURED_CLASS);
          featured.style.removeProperty('order');
        }
        item.classList.add(FEATURED_CLASS);
        item.style.order = '-1';
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }
}

if (!customElements.get('s-gallery-color-sync')) {
  customElements.define('s-gallery-color-sync', SGalleryColorSync);
}
